import React, { useState, useEffect } from 'react';
import { notify } from './Notifications';
import { useMsal } from '@azure/msal-react';
import ReactMarkdown from 'react-markdown';
import { givePrivacyConsent } from '../services/api';
import './PrivacyPolicyModal.css';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onConsent: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onConsent }) => {
  const { instance } = useMsal();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [privacyPolicyContent, setPrivacyPolicyContent] = useState<string>('');
  const [privacyPolicyVersion, setPrivacyPolicyVersion] = useState<string>('1.0');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadPrivacyPolicy = async () => {
      try {
        const response = await fetch('/privacy-policy.md');
        const content = await response.text();
        
        // Parse frontmatter to extract version
        const frontmatterMatch = content.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
        if (frontmatterMatch) {
          const frontmatter = frontmatterMatch[1];
          const bodyContent = frontmatterMatch[2];
          
          // Extract version from frontmatter
          const versionMatch = frontmatter.match(/version:\s*["']?([^"'\n]+)["']?/);
          if (versionMatch) {
            setPrivacyPolicyVersion(versionMatch[1].trim());
          }
          
          setPrivacyPolicyContent(bodyContent);
        } else {
          // No frontmatter, use entire content
          setPrivacyPolicyContent(content);
        }
      } catch (error) {
        console.error('Failed to load generative AI policy:', error);
        setPrivacyPolicyContent('Generative AI Policy content could not be loaded. Please contact support.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadPrivacyPolicy();
    }
  }, [isOpen]);

  const handleConsent = async () => {
    setIsSubmitting(true);
    try {
      await givePrivacyConsent(privacyPolicyVersion);
      localStorage.setItem('privacy_consent_given', 'true');
      localStorage.setItem('privacy_consent_version', privacyPolicyVersion);
      onConsent();
    } catch (error) {
      console.error('Error giving consent:', error);
      notify('Failed to record consent. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('privacy_consent_given');
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
      mainWindowRedirectUri: window.location.origin
    });
  };

  if (!isOpen) return null;

  return (
    <div className="privacy-modal-overlay">
      <div className="privacy-modal">
        <div className="privacy-modal-header">
          <h2>Generative AI Policy</h2>
        </div>
        <div className="privacy-modal-content">
          <div className="privacy-policy-text">
            {isLoading ? (
              <div className="loading-message">Loading generative AI policy...</div>
            ) : (
              <ReactMarkdown>{privacyPolicyContent}</ReactMarkdown>
            )}
          </div>
        </div>
        <div className="privacy-modal-footer">
          <button 
            className="consent-button primary"
            onClick={handleConsent}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Recording Consent...' : 'I Consent'}
          </button>
          <button 
            className="logout-button secondary"
            onClick={handleLogout}
            disabled={isSubmitting}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;